/**
 * useAvailableStatuses Hook
 * Provides the list of order statuses for the status filter dropdown.
 * Uses a static list matching the OrderStatus enum so the dropdown
 * always shows the same statuses on every environment (local, QA, prod).
 *
 * @author SAUBHAGYA Development Team
 */

import { useState } from 'react';
import type { OrderStatus } from '@/types/order';

export interface UseAvailableStatusesReturn {
  availableStatuses: OrderStatus[];
  isLoading: boolean;
  error: string | null;
}

/**
 * All order statuses in workflow order.
 * This is a static list matching the OrderStatus type definition
 * so the filter always renders correctly regardless of API availability.
 */
const ALL_ORDER_STATUSES: OrderStatus[] = [
  'ORDER_INITIALIZED',
  'PENDING',
  'CONFIRMED',
  'READY',
  'TRANSPORT_REQUESTED',
  'IN_PROGRESS',
  'IN_TRANSIT',
  'DELIVERED',
  'AWAITING_CUSTOMER_APPROVAL',
  'COMPLETED',
  'CANCELLED',
];

/**
 * Hook that returns the available order statuses for the filter dropdown.
 * Returns a static list so the dropdown always shows all statuses
 * consistently across local, QA, and production environments.
 */
export function useAvailableStatuses(
  _searchTerm?: string,
  _customerType?: string,
  _location?: { city: string | null; state: string | null }
): UseAvailableStatusesReturn {
  const [availableStatuses] = useState<OrderStatus[]>(ALL_ORDER_STATUSES);

  return {
    availableStatuses,
    isLoading: false,
    error: null,
  };
}

export default useAvailableStatuses;
